import { useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  useColorMode,
  Icon,
  Divider,
  Text,
} from '@chakra-ui/react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { Share2 } from 'lucide-react'
import { SecurityConfig } from '@/pages/HomePage/components/SecurityConfig'
import { PasscodeConfig } from '@/pages/HomePage/components/PasscodeConfig'
import type { SecurityOptions, PasscodeOptions } from '@/types/share'

// Turnstile site key - use test key in development
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  securityOptions: SecurityOptions
  onSecurityOptionsChange: (options: SecurityOptions) => void
  passcodeOptions: PasscodeOptions
  onPasscodeOptionsChange: (options: PasscodeOptions) => void
  turnstileToken: string | null
  onTurnstileSuccess: (token: string) => void
  onTurnstileError: () => void
  onTurnstileExpire: () => void
  onShare: () => void
  isLoading: boolean
  hasContent: boolean
  tier?: 'free' | 'starter' | 'pro'
}

export function ShareModal({
  isOpen,
  onClose,
  securityOptions,
  onSecurityOptionsChange,
  passcodeOptions,
  onPasscodeOptionsChange,
  turnstileToken,
  onTurnstileSuccess,
  onTurnstileError,
  onTurnstileExpire,
  onShare,
  isLoading,
  hasContent,
  tier = 'free',
}: ShareModalProps) {
  const { colorMode } = useColorMode()
  const turnstileRef = useRef<TurnstileInstance>(null)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={colorMode === 'dark' ? 'stone.800' : 'white'}>
        <ModalHeader display="flex" alignItems="center" gap={2}>
          <Icon as={Share2} boxSize={5} />
          Share Markdown
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Passcode config */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>
                Privacy
              </Text>
              <PasscodeConfig
                options={passcodeOptions}
                onChange={onPasscodeOptionsChange}
                tier={tier}
              />
            </Box>

            <Divider />

            {/* Security config */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>
                Security Options
              </Text>
              <SecurityConfig
                options={securityOptions}
                onChange={onSecurityOptionsChange}
                isPublic={passcodeOptions.isPublic}
                tier={tier}
              />
            </Box>

            {/* Turnstile - only required for free tier */}
            {tier === 'free' && (
              <>
                <Divider />
                <Box display="flex" justifyContent="center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={onTurnstileSuccess}
                    onError={onTurnstileError}
                    onExpire={onTurnstileExpire}
                    options={{
                      theme: colorMode === 'dark' ? 'dark' : 'light',
                      size: 'normal',
                    }}
                  />
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <Button
            colorScheme="brand"
            leftIcon={<Icon as={Share2} boxSize={4} />}
            onClick={onShare}
            isLoading={isLoading}
            isDisabled={!hasContent || (tier === 'free' && !turnstileToken)}
          >
            Publish Markdown
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
